/**
 * StoreSuccessPage — buyer-facing order confirmation + receipt.
 *
 * Loads the store_payment_intent created during checkout (via
 * stripe_checkout_session_id from the Stripe redirect), then all
 * child store_orders, items, and vendor display info. Shows the
 * single buyer charge at the top and a per-vendor allocation
 * breakdown below so the buyer can see exactly how their payment
 * was split. Acts as the printable receipt for the whole purchase.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import StoreSubnav from "@/components/store/StoreSubnav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, Printer, Package, ChevronRight } from "lucide-react";
import { useStoreCart, formatPrice } from "@/store/storeCart";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";

type Item = {
  id: string;
  product_title: string;
  variant_title: string | null;
  product_type: string;
  quantity: number;
  unit_price_cents: number;
  subtotal_cents: number;
};

type Order = {
  id: string;
  order_number: string;
  vendor_id: string;
  status: string;
  subtotal_cents: number;
  tax_cents: number;
  shipping_cents: number;
  total_cents: number;
  platform_fee_cents: number;
  vendor_payout_cents: number;
  shipping_address: Record<string, unknown> | null;
  store_order_items: Item[];
  store_vendors_public: { id: string; display_name: string; slug: string } | null;
};

type Pi = {
  id: string;
  amount_total_cents: number;
  platform_fee_cents: number;
  currency: string;
  status: string;
  succeeded_at: string | null;
  created_at: string;
  buyer_email: string | null;
  shipping_address: Record<string, unknown> | null;
  stripe_payment_intent_id: string | null;
};

const StoreSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const clear = useStoreCart((s) => s.clear);
  const { user } = useAuth();

  const [pi, setPi] = useState<Pi | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionId) clear();
  }, [sessionId, clear]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!sessionId || !user) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);

      // Webhook may take a moment — poll up to ~10s for the PI to
      // appear and flip to succeeded.
      let piRow: Pi | null = null;
      for (let attempt = 0; attempt < 6; attempt++) {
        const { data } = await supabase
          .from("store_payment_intents")
          .select(
            "id, amount_total_cents, platform_fee_cents, currency, status, succeeded_at, created_at, buyer_email, shipping_address, stripe_payment_intent_id",
          )
          .eq("stripe_checkout_session_id", sessionId)
          .maybeSingle();
        if (data) {
          piRow = data as Pi;
          if (data.status === "succeeded") break;
        }
        await new Promise((r) => setTimeout(r, 1500));
        if (cancelled) return;
      }

      if (!piRow) {
        if (!cancelled) {
          setError(
            "We couldn't find this order yet. Refresh in a moment, or check your order history.",
          );
          setLoading(false);
        }
        return;
      }

      const { data: orderRows, error: oErr } = await supabase
        .from("store_orders")
        .select(
          `id, order_number, vendor_id, status, subtotal_cents, tax_cents, shipping_cents,
           total_cents, platform_fee_cents, vendor_payout_cents, shipping_address,
           store_order_items ( id, product_title, variant_title, product_type, quantity, unit_price_cents, subtotal_cents ),
           store_vendors_public ( id, display_name, slug )`,
        )
        .eq("payment_intent_id", piRow.id)
        .order("created_at", { ascending: true });

      if (cancelled) return;
      if (oErr) {
        setError(oErr.message);
        setLoading(false);
        return;
      }

      setPi(piRow);
      setOrders((orderRows ?? []) as unknown as Order[]);
      setLoading(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [sessionId, user]);

  const totals = useMemo(() => {
    if (!orders.length) return null;
    return orders.reduce(
      (acc, o) => ({
        subtotal: acc.subtotal + o.subtotal_cents,
        tax: acc.tax + (o.tax_cents ?? 0),
        shipping: acc.shipping + (o.shipping_cents ?? 0),
        platform_fee: acc.platform_fee + o.platform_fee_cents,
        vendor_payout: acc.vendor_payout + o.vendor_payout_cents,
        total: acc.total + o.total_cents,
      }),
      { subtotal: 0, tax: 0, shipping: 0, platform_fee: 0, vendor_payout: 0, total: 0 },
    );
  }, [orders]);

  const currency = pi?.currency ?? "usd";
  const charged = pi?.amount_total_cents ?? totals?.total ?? 0;
  const orderDate = pi?.succeeded_at ?? pi?.created_at;

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <StoreSubnav />
      <main className="flex-1 mx-auto w-full max-w-3xl px-6 py-10 print:py-4">
        {/* Hero confirmation */}
        <Card className="p-8 text-center print:shadow-none print:border-0">
          <CheckCircle2 className="h-10 w-10 text-primary mx-auto" />
          <h1 className="mt-3 text-3xl font-bold">Thank you for your order</h1>
          <p className="mt-2 text-muted-foreground">
            Your single payment is confirmed. Below is your receipt and how
            funds were allocated to each vendor.
          </p>
          {pi && (
            <p className="mt-4 text-3xl font-bold tracking-tight">
              {formatPrice(charged, currency)}
              <span className="block text-xs font-normal text-muted-foreground mt-1">
                charged on {new Date(orderDate ?? Date.now()).toLocaleString()}
                {pi.stripe_payment_intent_id && (
                  <> · ref {pi.stripe_payment_intent_id}</>
                )}
              </span>
            </p>
          )}
          <div className="mt-6 flex flex-wrap gap-3 justify-center print:hidden">
            <Link to="/store/orders">
              <Button>View your orders</Button>
            </Link>
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" /> Print receipt
            </Button>
            <Link to="/store">
              <Button variant="ghost">Keep shopping</Button>
            </Link>
          </div>
        </Card>

        {loading && (
          <div className="mt-6 space-y-3">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        )}

        {error && !loading && (
          <Card className="mt-6 p-6 text-sm text-muted-foreground">
            {error}
          </Card>
        )}

        {!loading && pi && orders.length > 0 && (
          <>
            {/* Per-vendor allocation */}
            <section className="mt-8">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Allocated to {orders.length} vendor{orders.length === 1 ? "" : "s"}
              </h2>
              <div className="mt-3 space-y-4">
                {orders.map((o) => (
                  <Card key={o.id} className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">
                          Order {o.order_number}
                        </p>
                        <Link
                          to={
                            o.store_vendors_public
                              ? `/store/vendor/${o.store_vendors_public.slug}`
                              : "#"
                          }
                          className="font-semibold hover:underline"
                        >
                          {o.store_vendors_public?.display_name ?? "Vendor"}
                        </Link>
                        <p className="text-xs text-muted-foreground mt-0.5 capitalize">
                          Status: {o.status}
                        </p>
                      </div>
                      <Link
                        to={`/store/orders/${o.id}`}
                        className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center print:hidden"
                      >
                        Details <ChevronRight className="h-3 w-3" />
                      </Link>
                    </div>

                    <ul className="mt-4 space-y-2">
                      {o.store_order_items.map((it) => (
                        <li key={it.id} className="flex items-start justify-between gap-3 text-sm">
                          <div className="min-w-0">
                            <p className="truncate">
                              {it.product_title}
                              {it.variant_title && (
                                <span className="text-muted-foreground"> — {it.variant_title}</span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Qty {it.quantity} ·{" "}
                              {formatPrice(it.unit_price_cents, currency)} each ·{" "}
                              <span className="capitalize">{it.product_type}</span>
                            </p>
                          </div>
                          <p className="font-medium whitespace-nowrap">
                            {formatPrice(it.subtotal_cents, currency)}
                          </p>
                        </li>
                      ))}
                    </ul>

                    <hr className="my-4 border-border" />
                    <dl className="text-sm space-y-1">
                      <Row
                        label="Vendor subtotal"
                        value={formatPrice(o.subtotal_cents, currency)}
                      />
                      {o.tax_cents > 0 && (
                        <Row label="Tax" value={formatPrice(o.tax_cents, currency)} />
                      )}
                      {o.shipping_cents > 0 && (
                        <Row label="Shipping" value={formatPrice(o.shipping_cents, currency)} />
                      )}
                      <Row
                        label="Platform fee (Wavlynq)"
                        value={`− ${formatPrice(o.platform_fee_cents, currency)}`}
                        muted
                      />
                      <Row
                        label="Paid to vendor"
                        value={formatPrice(o.vendor_payout_cents, currency)}
                        bold
                      />
                    </dl>
                  </Card>
                ))}
              </div>
            </section>

            {/* Combined receipt totals */}
            {totals && (
              <Card className="mt-8 p-6">
                <h2 className="font-semibold">Receipt summary</h2>
                <dl className="mt-3 space-y-1.5 text-sm">
                  <Row label="Items subtotal" value={formatPrice(totals.subtotal, currency)} />
                  {totals.tax > 0 && (
                    <Row label="Tax" value={formatPrice(totals.tax, currency)} />
                  )}
                  {totals.shipping > 0 && (
                    <Row label="Shipping" value={formatPrice(totals.shipping, currency)} />
                  )}
                  <Row
                    label="Platform fee (kept by Wavlynq)"
                    value={formatPrice(totals.platform_fee, currency)}
                    muted
                  />
                  <Row
                    label="Net to vendors"
                    value={formatPrice(totals.vendor_payout, currency)}
                    muted
                  />
                  <hr className="my-2 border-border" />
                  <Row
                    label="Total charged to your card"
                    value={formatPrice(charged, currency)}
                    bold
                    large
                  />
                </dl>
                <p className="mt-4 text-xs text-muted-foreground leading-relaxed">
                  Wavlynq processed a single charge for this purchase and is
                  settling each vendor automatically. Each vendor sees only
                  their own order. A copy of this receipt has also been sent to{" "}
                  <span className="font-medium">{pi.buyer_email ?? "your email on file"}</span>.
                </p>
              </Card>
            )}

            {/* Shipping address (if any) */}
            {pi.shipping_address && (
              <Card className="mt-6 p-6">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  <h2 className="font-semibold">Shipping to</h2>
                </div>
                <ShippingDisplay addr={pi.shipping_address} />
              </Card>
            )}
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

const Row = ({
  label,
  value,
  muted,
  bold,
  large,
}: {
  label: string;
  value: string;
  muted?: boolean;
  bold?: boolean;
  large?: boolean;
}) => (
  <div className="flex justify-between">
    <dt className={muted ? "text-muted-foreground" : undefined}>{label}</dt>
    <dd
      className={[
        muted ? "text-muted-foreground" : "",
        bold ? "font-semibold" : "",
        large ? "text-base" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {value}
    </dd>
  </div>
);

const ShippingDisplay = ({ addr }: { addr: Record<string, unknown> }) => {
  // Supports both buyer_input shape and Stripe shipping_details shape
  const a = addr as any;
  const name = a.recipient_name ?? a.name ?? "";
  const inner = a.address ?? a;
  const line1 = inner.line1 ?? "";
  const line2 = inner.line2 ?? null;
  const city = inner.city ?? "";
  const region = inner.state ?? inner.region ?? "";
  const postal = inner.postal_code ?? "";
  const country = inner.country ?? "";
  return (
    <address className="not-italic mt-2 text-sm text-muted-foreground leading-6">
      {name && <div className="text-foreground">{name}</div>}
      <div>{line1}</div>
      {line2 && <div>{line2}</div>}
      <div>
        {city}
        {region ? `, ${region}` : ""} {postal}
      </div>
      <div>{country}</div>
    </address>
  );
};

export default StoreSuccessPage;
